import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { supabase } from '@/lib/supabaseClient';
import type { TimetableSlot, VacationSlot, VacationTerm } from '@/lib/types';
import Modal from '@/components/base/Modal';

type Source = 'empty' | 'regular' | 'copy';

type Props = {
  open: boolean;
  academyId: string | null;
  pastTerms: VacationTerm[];
  defaultName?: string;
  defaultSource?: Source;
  defaultCopyTermId?: string;
  onClose: () => void;
  onStarted: () => void;
};

const SOURCE_OPTIONS: { value: Source; label: string; description: string; icon: string }[] = [
  {
    value: 'regular',
    label: '정규 시간표 불러오기',
    description: '지금 정규 시간표를 그대로 복사해서 시작해요',
    icon: 'ri-file-copy-line',
  },
  {
    value: 'copy',
    label: '이전 방학특강 복사',
    description: '종료된 방학특강 시간표를 그대로 다시 사용해요',
    icon: 'ri-history-line',
  },
  {
    value: 'empty',
    label: '빈 시간표로 시작',
    description: '아무 일정 없이 새로 만들어요',
    icon: 'ri-add-line',
  },
];

export default function VacationTermStartModal({
  open,
  academyId,
  pastTerms,
  defaultName,
  defaultSource,
  defaultCopyTermId,
  onClose,
  onStarted,
}: Props) {
  const [name, setName] = useState('');
  const [source, setSource] = useState<Source>('regular');
  const [copyTermId, setCopyTermId] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(defaultName || '');
    const fallback: Source = pastTerms.length > 0 ? 'copy' : 'regular';
    setSource(defaultSource || fallback);
    setCopyTermId(defaultCopyTermId || pastTerms[0]?.id || '');
    setFormError('');
    // 열릴 때 한 번만 초기화해요.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name.trim()) {
      setFormError('방학특강 이름을 입력해 주세요.');
      return;
    }
    if (source === 'copy' && !copyTermId) {
      setFormError('복사할 방학특강을 선택해 주세요.');
      return;
    }
    setSaving(true);
    setFormError('');

    try {
      const { data: term, error: termError } = await supabase
        .from('vacation_terms')
        .insert({ academy_id: academyId || null, name: name.trim(), status: 'active' })
        .select()
        .maybeSingle();
      if (termError) throw termError;
      if (!term) throw new Error('term not created');
      const termId = (term as VacationTerm).id;

      let payload: Record<string, unknown>[] = [];

      if (source === 'regular') {
        const { data, error } = await supabase
          .from('timetable')
          .select('*')
          .order('day_of_week', { ascending: true })
          .order('start_time', { ascending: true });
        if (error) throw error;
        const regular = (data as TimetableSlot[]) || [];
        payload = regular.map((s) => ({
          academy_id: academyId || null,
          term_id: termId,
          class_id: s.class_id,
          name: null,
          subject: null,
          teacher_id: null,
          day_of_week: s.day_of_week,
          start_time: s.start_time,
          end_time: s.end_time,
        }));
      } else if (source === 'copy') {
        const { data, error } = await supabase
          .from('vacation_timetable')
          .select('*')
          .eq('term_id', copyTermId)
          .order('day_of_week', { ascending: true })
          .order('start_time', { ascending: true });
        if (error) throw error;
        const rows = (data as VacationSlot[]) || [];
        payload = rows.map((s) => ({
          academy_id: academyId || null,
          term_id: termId,
          class_id: s.class_id,
          name: s.name,
          subject: s.subject,
          teacher_id: s.teacher_id,
          day_of_week: s.day_of_week,
          start_time: s.start_time,
          end_time: s.end_time,
        }));
      }

      if (payload.length > 0) {
        const { error } = await supabase.from('vacation_timetable').insert(payload);
        if (error) throw error;
      }

      onStarted();
    } catch (err) {
      setFormError('방학특강을 시작하지 못했어요. 잠시 후 다시 시도해 주세요.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200';

  return (
    <Modal open={open} title="방학특강 시작" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground-700">
            방학특강 이름 <span className="text-accent-600">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="예: 2026 여름방학특강"
            className={inputClass}
          />
          <p className="mt-1 text-xs text-foreground-400">
            시작하면 이 이름으로 운영되고, 출결이 방학특강 기준으로 바뀌어요.
          </p>
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground-700">
            시작 방식
          </label>
          <div className="space-y-2">
            {SOURCE_OPTIONS.map((opt) => {
              const active = source === opt.value;
              const disabled = opt.value === 'copy' && pastTerms.length === 0;
              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={disabled}
                  onClick={() => setSource(opt.value)}
                  className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                    active
                      ? 'border-primary-400 bg-primary-50'
                      : 'border-background-200 bg-white hover:bg-background-50'
                  }`}
                >
                  <span
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                      active ? 'border-primary-500' : 'border-background-300'
                    }`}
                  >
                    {active && <span className="h-2.5 w-2.5 rounded-full bg-primary-500"></span>}
                  </span>
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                    <i className={`${opt.icon} text-base`}></i>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-foreground-900">
                      {opt.label}
                    </span>
                    <span className="block text-xs text-foreground-500">
                      {disabled ? '종료된 방학특강이 없어요' : opt.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {source === 'copy' && pastTerms.length > 0 && (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-700">
              복사할 방학특강
            </label>
            <select
              value={copyTermId}
              onChange={(e) => setCopyTermId(e.target.value)}
              className={inputClass}
            >
              {pastTerms.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {formError && (
          <div className="rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-800">
            {formError}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <i className="ri-play-circle-line"></i>
            {saving ? '시작 중...' : '방학특강 시작'}
          </button>
        </div>
      </form>
    </Modal>
  );
}